<template>
  <div class="gs-app gm-list-analytics-refresh flex mb-4 mx-4">
    <!-- Loader -->
    <portal to="loaderTarget">
      <div class="absolute bg-grey-100 bg-opacity-50 h-full left-0 top-0 w-full z-99" v-if="isLoading">
        <gd-loader :label="$t('frontend_loading')"/>
      </div>
    </portal>

    <div class="w-full gm-list-analytics-refresh__workspace">
      <navigation-tabs
        class="gm-nav-list-analytics-tabs absolute"
        :nav_tabs="navTabs"
      ></navigation-tabs>
      <server-table
        class="text-center groovemail-list-analytics-table gs-refresh-controls"
        ref="serverTable"
        :url="mailListUrl"
        :slotData="slotData"
        :tableRef="'listsTable'"
        :tableColumns="mailListsTableColumns"
        :tableOptions="mailListsTableOptions"
        :tableTitle="'frontend_groovemail_list_analytics'"
        :emptyStateData="emptyStateData"
        @loaded="hideLoader()"
        @loading="showLoader()"
      >
      </server-table>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import axios from 'axios';
import ServerTable from '@groovepages/gd-ui-kit/components/tables/ServerTable.vue';
import NavigationTabs from '@/components/navigation-tabs.vue';
import monthsMixin from '@/mixin/monthsMixin.js';
import { listUrls } from '@/utils/urls.js';
import { isKeyTimedOut } from '@/utils/localStorageHelper';
import { refreshMailObject } from '@/utils/refreshHelper';

let self;

export default {
  name: 'ListAnalytics',
  middleware: 'auth',

  metaInfo() {
    return { title: this.$t('frontend_groovemail_list_analytics') };
  },

  mixins: [monthsMixin],

  data() {
    return {
      totalLists: 0,
      mailListUrl: listUrls.list,
      isLoading: false,
      mailListsTableColumns: [
        'refresh',
        'name',
        'subscribe_leads_count',
        'unsubscribe_leads_count',
        'activeleads_count',
        'created_at',
        'actions',
      ],
      emptyStateData: {
        emptyStateHeading: 'frontend_groovemail_list_analytics',
        emptyStateSubHeading: 'frontend_groovemail_list_analytics_have_no_data',
      },
    };
  },

  components: {
    NavigationTabs,
    ServerTable,
  },

  created() {
    self = this;
  },

  methods: {
    // show loader
    showLoader() {
      if (!this.isLoading) {
        this.isLoading = true;
      }
    },

    // hide loader
    hideLoader (){
      if (this.isLoading) {
        this.isLoading = false;
      }
    },

    /**
     * Redirects user to contact listing page. Displays details of contacts present in that list.
     * @param {object} list contains details of a list.
     */
    async goToFilteredLeads(list) {
      this.$router.push({
        name: 'Leads2',
        query: { listId: list.id, listName: list.name },
      });
    },

    /**
     * Redirects user to analytics detail page.
     * @param {string} list contains unique id of list.
     */
    goToListStats(Id) {
      this.$router.push({ name: 'ListAnalyticsDetail', params: { id: Id } });
    },

    getRowIndex(listData, id) {
      return listData.findIndex((element) => element.id === id);
    },

    updateRowData(id, data) {
      const mailListData = this.$refs.serverTable.$refs.listsTable?.data;
      const rowIndex = this.getRowIndex(mailListData, id);
      mailListData[rowIndex] = {
        id,
        ...data,
        disabled: true,
      };
      this.$refs.serverTable.updateData(mailListData, this.totalLists);
    },

    async refreshMailList(id) {
      const url = `${listUrls.listRefresh}/${id}`;
      const response = await refreshMailObject(id, url, 'mailList');
      if (response) {
        this.updateRowData(id, response);
      }
    },
  },

  computed: {
    /**
     * Returns navigation tabs data for lead analytics page.
     * @return {array} details for navigation tabs for lead analytics page.
    */
    navTabs() {
      return [
          {
            tabName: 'frontend_groovemail_leads_route_page_title',
            tabRoute: 'LeadAnalytics',
          },
          {
            tabName: 'frontend_groovemail_lists_route_page_title',
            tabRoute: 'ListAnalytics',
          },
          {
            tabName: 'frontend_groovemail_broadcasts_route_page_title',
            tabRoute: 'BroadcastAnalytics',
          },
          {
            tabName: 'frontend_groovemail_sequences_route_page_title',
            tabRoute: 'SequenceAnalytics',
          },
        ];
    },

    /**
     * Returns table options for list analytics table.
     * @return {object} table options for list analytics table.
     */
     mailListsTableOptions() {
      return {
        sortIcon: {
          base: 'fa',
          is: 'fa-sort',
          up: 'fa-sort-asc',
          down: 'fa-sort-desc',
        },
        sortable: [
          'name',
          'activeleads_count',
          'subscribe_leads_count',
          'unsubscribe_leads_count',
        ],
        texts: {
          filterPlaceholder: this.$t('frontend_groovemail_tables_search_query'),
        },
        filterable: ['name'],
        perPage: 10,
        debounce: 1000,
        alwaysShowPerPageSelect: true,
        perPageValues: [10, 25, 50, 100],
        requestFunction(data) {
          const self = this;
          const requestParams = { ...data };
          if (!(requestParams && requestParams.orderBy)) {
            requestParams.orderBy = 'created_at';
            requestParams.ascending = 0;
          }
          return axios
            .get(self.url, {
              params: requestParams,
            })
            .catch((e) => {
              self.dispatch('error', e);
              self.hideLoader();
            });
        },
        responseAdapter({ data }) {
          const listTableData = data?.data.map((row) => {
            return {
              ...row,
              disabled: isKeyTimedOut('mailList', row.id),
            };
          });
          self.totalLists = data.total;
          return {
            data: listTableData,
            count: data.total,
          };
        },
        headings: {
          refresh: () => '',
          name: () => this.$t('frontend_groovemail_adminsetting_name'),
          activeleads_count: () => this.$t('frontend_groovemail_components_checkout_total'),
          subscribe_leads_count: () => this.$t('frontend_groovemail_segment_subscribe_emails'),
          unsubscribe_leads_count: () => this.$t('frontend_groovemail_lead_listing_advanced_filters_status_unsubscribed'),
          created_at: () => this.$t('frontend_groovemail_created_at'),
          actions: () => this.$t('frontend_groovemail_mail_lead_actions'),
        },
        rowClassCallback: () => 'hover:bg-grey-50 funnelRow',
      };
    },

    /**
     * Returns navigation tabs data for lead analytics page.
     * @return {array} details for navigation tabs for lead analytics page.
    */
    navTabs() {
      return [
          {
            tabName: 'frontend_groovemail_leads_route_page_title',
            tabRoute: 'LeadAnalytics',
          },
          {
            tabName: 'frontend_groovemail_lists_route_page_title',
            tabRoute: 'ListAnalytics',
          },
          {
            tabName: 'frontend_groovemail_broadcasts_route_page_title',
            tabRoute: 'BroadcastAnalytics',
          },
          {
            tabName: 'frontend_groovemail_sequences_route_page_title',
            tabRoute: 'SequenceAnalytics',
          },
        ];
    },

    /**
     * Returns table options for list analytics table.
     * @return {object} table options for list analytics table.
     */
     mailListsTableOptions() {
      return {
        sortIcon: {
          base: 'fa',
          is: 'fa-sort',
          up: 'fa-sort-asc',
          down: 'fa-sort-desc',
        },
        sortable: [
          'name',
          'activeleads_count',
          'subscribe_leads_count',
          'unsubscribe_leads_count',
        ],
        texts: {
          filterPlaceholder: this.$t('frontend_groovemail_tables_search_query'),
        },
        filterable: ['name'],
        perPage: 10,
        debounce: 1000,
        alwaysShowPerPageSelect: true,
        perPageValues: [10, 25, 50, 100],
        requestFunction(data) {
          const self = this;
          const requestParams = { ...data };
          if (!(requestParams && requestParams.orderBy)) {
            requestParams.orderBy = 'created_at';
            requestParams.ascending = 0;
          }
          return axios
            .get(self.url, {
              params: requestParams,
            })
            .catch((e) => {
              self.dispatch('error', e);
              self.$parent.loadedTable();
            });
        },
        responseAdapter({ data }) {
          const listTableData = data?.data.map((row) => {
            return {
              ...row,
              disabled: isKeyTimedOut('mailList', row.id),
            };
          });
          self.totalLists = data.total;
          return {
            data: listTableData,
            count: data.total,
          };
        },
        headings: {
          refresh: () => '',
          name: () => this.$t('frontend_groovemail_adminsetting_name'),
          activeleads_count: () => this.$t('frontend_groovemail_components_checkout_total'),
          subscribe_leads_count: () => this.$t('frontend_groovemail_segment_subscribe_emails'),
          unsubscribe_leads_count: () => this.$t('frontend_groovemail_lead_listing_advanced_filters_status_unsubscribed'),
          created_at: () => this.$t('frontend_groovemail_created_at'),
          actions: () => this.$t('frontend_groovemail_mail_lead_actions'),
        },
        rowClassCallback: () => 'hover:bg-grey-50 funnelRow',
      };
    },

    /**
     * Returns slot data for list analytics table.
     * @return {array} details for slot columns for list analytics table.
     */
    slotData() {
      return [
        {
          slotName: 'refresh',
          slotType: 'default',
          conditions: () => true,
          callback: (props) => {
            if (!props.row.disabled) {
              this.refreshMailList(props.row.id);
            }
            return null;
          },
          html: (props) => {
            const tooltipText = props.row.disabled
              ? this.$t('frontend_groovemail_disabled_refresh_button_tooltip')
              : this.$t('frontend_groovemail_refresh_button');

            const iconClass = props.row.disabled
              ? 'h-5 w-5 mt-1 refresh-icon-disabled'
              : 'h-5 w-5 mt-1 refresh-icon text-primary';

            const iconId = props.row.id ? `id="refreshIcon-${props.row.id}"` : '';

            const refreshIcon = `
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="${iconClass}" ${iconId}>
                <path fill="currentcolor" d="M12,2a10.032,10.032,0,0,1,7.122,3H16a1,1,0,0,0-1,1h0a1,1,0,0,0,1,1h4.143A1.858,1.858,0,0,0,22,5.143V1a1,1,0,0,0-1-1h0a1,1,0,0,0-1,1V3.078A11.981,11.981,0,0,0,.05,10.9a1.007,1.007,0,0,0,1,1.1h0a.982.982,0,0,0,.989-.878A10.014,10.014,0,0,1,12,2Z"/>
                <path fill="currentcolor" d="M22.951,12a.982.982,0,0,0-.989.878A9.986,9.986,0,0,1,4.878,19H8a1,1,0,0,0,1-1H9a1,1,0,0,0-1-1H3.857A1.856,1.856,0,0,0,2,18.857V23a1,1,0,0,0,1,1H3a1,1,0,0,0,1-1V20.922A11.981,11.981,0,0,0,23.95,13.1a1.007,1.007,0,0,0-1-1.1Z"/>
              </svg>
            `;

            return `
              <span class="tooltip-refresh-btn">
                ${refreshIcon}
                <span class="tooltiptext">${tooltipText}</span>
              </span>
            `;
          },
        },
        {
          slotName: 'name',
          slotType: 'computed',
          classes: () => '',
          value: (props) => props.row.name ? props.row.name : this.$t('frontend_groovemail_not_available'),
        },
        {
          slotName: 'subscribe_leads_count',
          slotType: 'computed',
          classes: () => '',
          value: (props) => props.row.subscribe_leads_count ? props.row.subscribe_leads_count : 0,
        },
        {
          slotName: 'activeleads_count',
          slotType: 'clickable',
          classes: () => '',
          callback: (props) => this.goToFilteredLeads(props.row),
          defaultValue: 0,
        },
        {
          slotName: 'unsubscribe_leads_count',
          slotType: 'computed',
          classes: () => '',
          value: (props) => props.row.unsubscribe_leads_count ? props.row.unsubscribe_leads_count : 0,
        },
        {
          slotName: 'created_at',
          slotType: 'computed',
          classes: () => '',
          value: (props) => props.row.created_at ?
            `${new Date(props.row.created_at).getDate()} ${this.months[new Date(props.row.created_at).getMonth()]},
            ${new Date(props.row.created_at).getFullYear()}` :
            this.$t('frontend_groovemail_not_available'),
        },
        {
          slotName: 'actions',
          slotType: 'actions',
          conditions: () => true,
          classes: () => {
            return {
              outerDiv: '',
              dropdown: '',
              outerSpan: '',
            };
          },
          actionList: [
            {
              id: 0,
              name: () => this.$t('frontend_groovemail_analytics_detailed_stats'),
              conditions: () => true,
              callback: (props) => this.goToListStats(props.row.id),
            },
          ],
        },
      ];
    },
  },
};
</script>


<style lang="scss">
.groovemail-list-analytics-table {
  .tooltip-refresh-btn {
    position: relative;
    display: inline-block;
    
    .tooltiptext {
      visibility: hidden;
      width: 120px;
      background-color: #fff;
      color: #000;
      text-align: center;
      border-radius: 6px;
      padding: 5px 0;
      /* Position the tooltip */
      position: absolute;
      z-index: 1;
      top: 30px;
      left: -50px;
      
    }
    
    &:hover .tooltiptext {
      visibility: visible;
    }
  }
  .refresh-icon {
    cursor: pointer;
    transition: transform 0.5s ease-in-out;

    &-disabled {
      cursor: pointer;
      color: #a8aaaf;
    }
    
    &.spin {
      animation: spin 0.5s infinite linear;
    }
  }


  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
  .VueTables--server table {
    overflow: auto !important;
    th:nth-child(2),
    td:nth-child(2),
    th:nth-child(3),
    td:nth-child(3) {
      width: 20%;
    }
    td:nth-child(4),
    th:nth-child(4),
    td:nth-child(5),
    th:nth-child(5),
    th:nth-child(6),
    td:nth-child(6),
    th:nth-child(7),
    td:nth-child(7) {
      width: 14%;
    }
    th:nth-child(8),
    td:nth-child(8) {
      width: 10%;
    }
    th:nth-child(7) span {
      display: none;
    }
  }
}

.gm-nav-list-analytics-tabs {
  top: 37px;
  z-index: 10;
}
</style>

<style scoped>
.gm-list-analytics-refresh {
  min-height: 100%;
  padding: 28px;
  color: var(--gs-foreground);
  background: var(--gs-background);
}

.gm-list-analytics-refresh__workspace {
  min-width: 0;
  padding: 20px 24px 24px;
  background: var(--gs-surface);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-lg);
  box-shadow: var(--gs-shadow-sm);
}

.gm-list-analytics-refresh >>> .VueTables__table,
.gm-list-analytics-refresh >>> .VueTables__table th,
.gm-list-analytics-refresh >>> .VueTables__table td {
  border-color: var(--gs-border);
}

@media (max-width: 640px) {
  .gm-list-analytics-refresh { padding: 18px 12px 32px; }
  .gm-list-analytics-refresh__workspace { padding: 16px; }
}
</style>
